import { useContext, useMemo } from "react"
import UserContext from "../contexts/UserContext/context"
import { useTranslation } from "react-i18next"
import { exportAreaMonitoringReport, exportDayOverviewReport, exportEmissionsReport, exportFuelPurchasesReport, exportIdleStopsReport, exportMaintenanceReport, exportSpeedViolationReport, exportTachographReport, exportTagReport, exportTemperatureQualityMonitoringReport, exportTemperatureQualityReport, exportTemperatureReport, exportVehicleDistanceReport, exportVehicleReport, exportWeeklyWorkingHoursReport, getAreaMonitoringReport, getDayOverviewReport, getEmissionsReport, getFuelPurchasesReport, getIdleStopsReport, getMaintenanceReport, getSpeedViolationReport, getTachographReport, getTagReport, getTemperatureQualityMonitoringReport, getTemperatureQualityReport, getTemperatureReport, getVehicleDistanceReport, getVehicleReport, getWeeklyWorkingHoursReport } from "../services/calls/reports"
import { REP_COLS } from "../pages/Reports/Data/columnData"

export enum REPORT {
  MOBILE_LIST = 'mobile-list',
  IDLE_STOPS = 'idle-stops',
  GRAPH = 'tachograph',
  TAGS = 'tags',
  DAY_OVERVIEW = 'day-overview',
  WEEKLY_HOURS = 'weekly-hours',
  FUEL_PURCHASES = 'fuel-purchases',
  EMISSIONS = 'emissions',
  TEMPERATURE = 'temperature',
  AREA_MONITORING = 'area-monitoring',
  SPEED_VIOLATIONS = 'speed-violations',
  TEMP_QUAL_MON = 'temperature-quality-monitoring',
  TEMP_QUAL = 'temperature-quality',
  MAINTENANCE = 'maintenance',
  VEHICLE_DISTANCE = 'vehicle-distance'
}

export const useReports = () => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { permissions } = user

  const reports = useMemo(() => ({
    [REPORT.MOBILE_LIST]: {
      name: t('Mobile list'),
      url: '/mobile-list',
      filters: ['fleet', 'mobiles'],
      funcFilters: {
        mobiles: 'mobileids',
      },
      getFunction: getVehicleReport,
      exportFunction: exportVehicleReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_mobilelist,
      columns: [
        REP_COLS.REGISTRY_NUMBER, REP_COLS.GLOBALID, REP_COLS.MOBILE_DESCR, REP_COLS.ODOMETER,
        REP_COLS.WORKING_HOURS, REP_COLS.DRIVER
      ]
    },
    [REPORT.IDLE_STOPS]: {
      name: t('Idle stops'),
      url: '/idle-stops',
      filters: ['fleet', 'mobiles', 'date'],
      funcFilters: {
        mobiles: 'mobileids',
        date: 'date',
      },
      getFunction: getIdleStopsReport,
      exportFunction: exportIdleStopsReport,
      isDynamic: true,
      hasTable: true,
      hasGraph: false,
      hasExpandRow: true,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_idlestops,
      columns: [
        REP_COLS.GLOBALID, REP_COLS.PERIOD, REP_COLS.TOTAL_IDLE_TIME, REP_COLS.STOP_COUNT,
        REP_COLS.AVG_IDLE_DURATION
      ]
    },
    [REPORT.GRAPH]: {
      name: t('Tachograph'),
      url: '/tachograph',
      filters: ['fleet', 'mobiles', 'filetype', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        },
        filetype: 'mobile_or_driver'
      },
      getFunction: getTachographReport,
      exportFunction: exportTachographReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_tachograph,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.CREATE_DATE, REP_COLS.FILE_TYPE, REP_COLS.FILE
      ]
    },
    [REPORT.TAGS]: {
      name: t('Tags'),
      url: '/tags',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getTagReport,
      exportFunction: exportTagReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_tags,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.TIME, REP_COLS.LOCATION, REP_COLS.RADIUS, REP_COLS.SIGNAL_STRENGTH
      ]
    },
    [REPORT.DAY_OVERVIEW]: {
      name: t('Day overview'),
      url: '/day-overview',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getDayOverviewReport,
      exportFunction: exportDayOverviewReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_dayoverview,
      columns: [
        REP_COLS.DATE, REP_COLS.VEHICLE, REP_COLS.STARTED_WORKING, REP_COLS.STOPPED_WORKING, REP_COLS.DISTANCE,
        REP_COLS.TOTAL_STOP_TIME, REP_COLS.TOTAL_IDLE_TIME, REP_COLS.DRIVING_TIME, REP_COLS. MAX_SPEED_OVER_LIMIT,
        REP_COLS.RESPONSIBLE, REP_COLS.SPEEDING
      ]
    },
    [REPORT.WEEKLY_HOURS]: {
      name: t('Weekly working hours'),
      url: '/weekly-hours',
      filters: ['fleet', 'mobiles', 'date'],
      funcFilters: {
        mobiles: 'mobileids',
        date: 'date',
      },
      getFunction: getWeeklyWorkingHoursReport,
      exportFunction: exportWeeklyWorkingHoursReport,
      isDynamic: true,
      hasTable: true,
      hasGraph: false,
      hasExpandRow: true,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_weeklyhours,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.PERIOD, REP_COLS.TOTAL_HOURS
      ]
    },
    [REPORT.FUEL_PURCHASES]: {
      name: t('Fuel purchases'),
      url: '/fuel-purchases',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getFuelPurchasesReport,
      exportFunction: exportFuelPurchasesReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_fuelpurchases,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.TRANSACTION_ID, REP_COLS.PURCHASE_DATE, REP_COLS.SITE_NAME, REP_COLS.FUEL_TYPE,
        REP_COLS.VOLUME, REP_COLS.AVERAGE_FUEL_CONSUMPTION, REP_COLS.DEVIATION_RECORDED
      ]
    },
    [REPORT.EMISSIONS]: {
      name: t('CO2 emissions'),
      url: '/emissions',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getEmissionsReport,
      exportFunction: exportEmissionsReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_emissions,
      columns: [
        REP_COLS.REGISTRY_NUMBER, REP_COLS.GLOBALID, REP_COLS.EMISSIONS_GKM, REP_COLS.TOTAL_DISTANCE,
        REP_COLS.TOTAL_EMISSIONS
      ]
    },
    [REPORT.TEMPERATURE]: {
      name: t('Temperature overview'),
      url: '/temperature',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getTemperatureReport,
      exportFunction: exportTemperatureReport,
      hasTable: false,
      hasGraph: true,
      permissions: !permissions?.trackwell?.cs_report_temperature,
      graph: {
        title: t('Temperature measurements'),
        subtitle: 'description',
        axis: {
          x: {
            type: 'datetime',
            title: t('Date')
          },
          y: {
            type: 'number',
            title: t('Temperature (˚C)')
          }
        },
        tooltip: '˚C',
        sortby: 'datadate',
        dataValue: 'temperature',
        min: 'lower_limit',
        max: 'upper_limit',
        yMin: -20,
        yMax: 30
      },
      seriesPath: 'sensordata'
    },
    [REPORT.AREA_MONITORING]: {
      name: t('Area monitoring'),
      url: '/area-monitoring',
      filters: ['fleet', 'mobiles', 'daterange', 'visitedareas', 'areas'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        },
        visitedareas: 'visited',
        areas: 'areaids'
      },
      getFunction: getAreaMonitoringReport,
      exportFunction: exportAreaMonitoringReport,
      hasTable: true,
      hasGraph: false,
      permissions: !permissions?.trackwell?.cs_report_areamonitoring,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.DATE_FROM, REP_COLS.DATE_TO, REP_COLS.AREA, REP_COLS.DURATION
      ]
    },
    [REPORT.SPEED_VIOLATIONS]: {
      name: t('Speed violations'),
      url: '/speed-violations',
      filters: ['fleet', 'mobiles', 'overlimit', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        },
        overlimit: 'minover'
      },
      getFunction: getSpeedViolationReport,
      exportFunction: exportSpeedViolationReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_speedviolations,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.TIME, REP_COLS.LOCATION, REP_COLS.SPEEDLIMIT, REP_COLS.SPEED
      ]
    },
    [REPORT.TEMP_QUAL_MON]: {
      name: t('Temperature quality monitoring'),
      url: '/temperature-quality-monitoring',
      filters: ['fleet', 'mobiles', 'weekrange'],
      funcFilters: {
        mobiles: 'mobileids',
        weekrange: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getTemperatureQualityMonitoringReport,
      exportFunction: exportTemperatureQualityMonitoringReport,
      hasTable: false,
      hasGraph: true,
      permissions: !permissions?.trackwell?.cs_report_temperature_quality_mon_form,
      graph: {
        title: t('Temperature measurements'),
        subtitle: 'description',
        axis: {
          x: {
            type: 'datetime',
            title: t('Date')
          },
          y: {
            type: 'number',
            title: t('Temperature (˚C)')
          }
        },
        tooltip: '˚C',
        sortby: 'datadate',
        dataValue: 'temperature',
        min: 'lower_limit',
        max: 'upper_limit',
        yMin: -20,
        yMax: 30
      },
      seriesPath: 'sensordata'
    },
    [REPORT.TEMP_QUAL]: {
      name: t('Temperature quality'),
      url: '/temperature-quality',
      filters: ['fleet', 'mobiles', 'weekrange'],
      funcFilters: {
        mobiles: 'mobileids',
        weekrange: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getTemperatureQualityReport,
      exportFunction: exportTemperatureQualityReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_temperature_quality_mon_report,
      columns: [
        REP_COLS.NAME, REP_COLS.CHECKED_FROM, REP_COLS.CHECKED_TO, REP_COLS.APPROVED, REP_COLS.COMMENT,
        REP_COLS.USER, REP_COLS.LAST_MODIFIED
      ]
    },
    [REPORT.MAINTENANCE]: {
      name: t('Maintenance'),
      url: '/maintenance',
      filters: ['fleet', 'mobiles', 'daterange'],
      funcFilters: {
        mobiles: 'mobileids',
        dates: {
          from: 'from_date',
          to: 'to_date',
        }
      },
      getFunction: getMaintenanceReport,
      exportFunction: exportMaintenanceReport,
      hasTable: true,
      hasGraph: false,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_maintenance,
      columns: [
        REP_COLS.VEHICLE, REP_COLS.SERVICE, REP_COLS.DESCRIPTION, REP_COLS.SERVICE_TYPE,
        REP_COLS.PLANNED_AT, REP_COLS.SERVICED_AT, REP_COLS.DATE, REP_COLS.COST, REP_COLS.MESSAGE,
        REP_COLS.COMMENT
      ]
    },
    [REPORT.VEHICLE_DISTANCE]: {
      name: t('Vehicle distance'),
      url: '/vehicle-distance',
      filters: ['fleet', 'mobiles', 'date'],
      funcFilters: {
        mobiles: 'mobileids',
        date: 'date',
      },
      getFunction: getVehicleDistanceReport,
      exportFunction: exportVehicleDistanceReport,
      isDynamic: true,
      hasTable: true,
      hasGraph: false,
      hasExpandRow: true,
      rowId: 'globalid',
      permissions: !permissions?.trackwell?.cs_report_vehicle_distance,
      columns: [
        REP_COLS.GLOBALID, REP_COLS.PERIOD_DISTANCE, REP_COLS.TOTAL
      ]
    }
  }), [permissions, t])

  const availableReports = useMemo(() => {
    return Object.keys(reports).reduce((obj, key) => {
      const report = reports[key]
      if (report?.permissions) obj[key] = report
      return obj
    }, {})
  }, [reports])

  return {
    reports: availableReports,
    showReports: Object.keys(availableReports).length > 0
  }
}
